import React, { useRef, useEffect, useState } from 'react';
import Quill from 'quill';
import { useThemeContext, useMessageInputContext, useTextInputContext, useFileInputContext } from '@gopeerproject/ui-kit';
import { EntityBlot, checkFormat, clipboardMatcher, linkFormatter } from '../helpers';
import { LinkTooltip } from './LinkTooltip';
Quill.register(EntityBlot);
export const Editor = ({ onTextChange, minNumberOfLines = 1, messageInputId }) => {
    const ref = useRef(null);
    const onEnter = useRef(null);
    const { colorScheme, size } = useThemeContext();
    const { send, setIsFocused } = useMessageInputContext();
    const { add } = useFileInputContext();
    const { editor, setEditor, setLastSelection, setTools, tools, toggleTool } = useTextInputContext();
    const [linkTooltip, setLinkTooltip] = useState();
    const quill = editor;
    useEffect(() => {
        onEnter.current = send;
    }, [send]);
    useEffect(() => {
        if (!quill)
            return;
        const onChange = () => {
            const next = checkFormat(quill, tools);
            if (next.tools)
                setTools(next.tools);
            if (next.linkTooltip)
                setLinkTooltip(next.linkTooltip);
        };
        quill.on('editor-change', onChange);
        return () => {
            quill.off('editor-change', onChange);
        };
    }, [tools, setTools, quill]);
    useEffect(() => {
        if (!quill || !onTextChange)
            return;
        const onChange = () => {
            if (quill.getText().trim().length === 0)
                return;
            onTextChange();
        };
        quill.on('text-change', onChange);
        return () => {
            quill.off('text-change', onChange);
        };
    }, [quill, onTextChange]);
    useEffect(() => {
        if (!quill)
            return;
        const onBlur = () => {
            setIsFocused(false);
        };
        const onFocus = () => {
            setIsFocused(true);
        };
        quill.root.addEventListener('blur', onBlur);
        quill.root.addEventListener('focus', onFocus);
        return () => {
            quill.root.removeEventListener('blur', onBlur);
            quill.root.removeEventListener('focus', onFocus);
        };
    }, [quill, setIsFocused]);
    useEffect(() => {
        const handler = (event) => {
            var _a, _b;
            const { items } = ((_a = event.clipboardData) !== null && _a !== void 0 ? _a : 
            // @ts-expect-error supported in new chrome
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            (_b = event.originalEvent) === null || _b === void 0 ? void 0 : _b.clipboardData);
            if (messageInputId) {
                const messageInputElem = document.getElementById(messageInputId);
                if (messageInputElem &&
                    !messageInputElem.contains(document.activeElement)) {
                    return;
                }
            }
            const files = [];
            // eslint-disable-next-line @typescript-eslint/prefer-for-of
            for (let i = 0; i < items.length; i++) {
                const item = items[i];
                if (item.kind === 'file') {
                    const file = item.getAsFile();
                    if (file)
                        files.push(file);
                }
            }
            add(files);
        };
        document.addEventListener('paste', handler);
        return () => {
            document.removeEventListener('paste', handler);
        };
    }, []);
    useEffect(() => {
        if (!ref.current)
            return;
        const bindings = {
            enter: {
                key: 13,
                handler: () => {
                    if (onEnter.current)
                        onEnter.current();
                }
            },
            tab: {
                key: 'tab',
                handler: () => void null
            },
            indent: {
                key: 'tab',
                format: ['list'],
                handler: () => void null
            }
        };
        const base = new Quill(ref.current, {
            modules: {
                keyboard: {
                    bindings
                },
                history: {
                    userOnly: true
                }
            },
            placeholder: 'Type message or drag & drop files'
        });
        linkFormatter(base);
        base.clipboard.addMatcher(Node.ELEMENT_NODE, clipboardMatcher);
        setEditor(base);
    }, []);
    return (React.createElement("div", { ref: ref, onClick: () => {
            if (tools.includes('link') && !tools.includes('link-tooltip'))
                toggleTool('link-tooltip');
        }, className: `gp-MessageInput ${colorScheme}`, style: { minHeight: minNumberOfLines * size.I + size.C * 2 }, onBlur: () => {
            if (!quill)
                return;
            setLastSelection(quill.getSelection());
        }, onFocus: () => {
            setLastSelection(null);
        } }, tools.includes('link-tooltip') && linkTooltip && (React.createElement(LinkTooltip, Object.assign({}, linkTooltip)))));
};
//# sourceMappingURL=Editor.js.map